import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { Slider } from "./ui/slider";
import { inr } from "../lib/api";
import { useSite } from "../lib/site";
import { OperatorBlock } from "./Layout";
import { useT } from "../lib/i18n";
import { ToshniwalProfile, PmSolarSection, ApnSection } from "./BrandProfiles";

const RATES = [["Starter", 0.1], ["Growth", 0.12], ["Network", 0.15]];

export function CommissionExample() {
  const [ercr, setErcr] = useState(20000);
  const { t } = useT();
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 shadow-sm" data-testid="commission-example">
      <p className="eyebrow mb-2">{t("commission.eyebrow")}</p>
      <label className="block text-sm font-medium text-slate-800 mb-3" htmlFor="ercr">{t("commission.label")} <span className="font-mono font-bold" data-testid="ercr-value">{inr(ercr)}</span></label>
      <Slider id="ercr" min={5000} max={100000} step={1000} value={[ercr]} onValueChange={(v) => setErcr(v[0])} data-testid="ercr-slider" />
      <div className="grid grid-cols-3 gap-3 mt-6">
        {RATES.map(([n, r]) => (
          <div key={n} className="rounded-xl bg-slate-50 border border-slate-200 p-4">
            <p className="text-xs text-slate-600">{t(`commission.${n}`)} ({r * 100}%)</p>
            <p className="font-mono text-lg sm:text-xl font-bold text-emerald-800" data-testid={`commission-${n.toLowerCase()}`}>{inr(ercr * r)}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-slate-600 mt-4">{t("commission.note")}</p>
    </div>
  );
}

const SLABS = [[1, 30000], [2, 60000], [3, 78000]];
export function SubsidyTable() {
  const { tl } = useT();
  return (
    <div data-testid="subsidy-table">
      <p className="text-slate-700 mb-3">{tl("Central Financial Assistance for residential rooftop solar: ₹30,000 per kW for the first 2 kW, ₹18,000 for the 3rd kW; no additional assistance above 3 kW. Special-category states have higher rates.")}</p>
      <table className="w-full text-sm border border-slate-200 rounded-lg overflow-hidden bg-white">
        <thead className="bg-slate-100"><tr><th className="text-left p-3">{tl("System size")}</th><th className="text-left p-3">{tl("Central assistance")}</th></tr></thead>
        <tbody>{SLABS.map(([k, v]) => <tr key={k} className="border-t"><td className="p-3">{k === 3 ? tl("3 kW and above") : `${k} kW`}</td><td className="p-3 font-mono">{inr(v)}</td></tr>)}</tbody>
      </table>
      <p className="text-xs text-slate-600 mt-2">{tl("Source: MNRE scheme guidelines and")} <a className="underline" href="https://pmsuryaghar.gov.in" target="_blank" rel="noopener noreferrer">pmsuryaghar.gov.in</a>. {tl("Last fact-check: 23 Oct 2026. Eligibility and approval are decided by the scheme authorities and your DISCOM.")}</p>
    </div>
  );
}

export function SizingCalc() {
  const [units, setUnits] = useState(300);
  const [gen, setGen] = useState(120);
  const { tl } = useT();
  const kw = units > 0 && gen > 0 ? (units / gen).toFixed(1) : "—";
  return (
    <div className="grid sm:grid-cols-3 gap-3 items-end bg-white border rounded-xl p-4" data-testid="sizing-calc">
      <label className="text-sm">{tl("Monthly units (kWh)")}<input type="number" className="mt-1 w-full border rounded-lg px-3 py-2" value={units} onChange={(e) => setUnits(+e.target.value)} data-testid="sizing-units" /></label>
      <label className="text-sm">{tl("Units per kW per month (estimate)")}<input type="number" className="mt-1 w-full border rounded-lg px-3 py-2" value={gen} onChange={(e) => setGen(+e.target.value)} data-testid="sizing-gen" /></label>
      <p className="text-sm">{tl("Indicative size:")} <span className="font-mono font-bold text-lg" data-testid="sizing-result">{kw} kW</span></p>
    </div>
  );
}

export function FaqList({ items }) {
  const { tl } = useT();
  return (
    <Accordion type="single" collapsible className="bg-white rounded-xl border px-4" data-testid="faq-list">
      {items.map(([q, a], i) => (
        <AccordionItem key={q} value={`q${i}`}>
          <AccordionTrigger className="text-left font-medium" data-testid={`faq-q-${i}`}>{tl(q)}</AccordionTrigger>
          <AccordionContent className="text-slate-700">{tl(a)}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

export function Slot({ name }) {
  if (name === "commission") return <CommissionExample />;
  if (name === "subsidy") return <SubsidyTable />;
  if (name === "sizing") return <SizingCalc />;
  if (name === "operator") return <OperatorBlock />;
  if (name === "toshniwal") return <ToshniwalProfile />;
  if (name === "pmsolar") return <PmSolarSection />;
  if (name === "apn") return <ApnSection />;
  return null;
}

export function useFill() {
  const s = useSite();
  return (t) => t.replace("{window}", s.exclusive_window_hours || 48).replace("{payoutDays}", s.payout_days_after_month_end || 15);
}

export function CtaLink({ to, children, variant = "primary", testid }) {
  const cls = variant === "primary" ? "bg-amber-700 hover:bg-amber-800 text-white shadow-md" : variant === "dark" ? "bg-slate-900 hover:bg-slate-800 text-white" : "border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white";
  return <Link to={to} data-testid={testid} className={`inline-flex items-center gap-2 font-semibold py-3 px-6 rounded-lg transition-colors active:scale-[0.98] ${cls}`}>{children}<ArrowRight className="h-4 w-4" /></Link>;
}

export function usePathCrumbs(names) {
  const { pathname } = useLocation();
  const parts = pathname.split("/").filter(Boolean);
  return parts.map((p, i) => ({ to: "/" + parts.slice(0, i + 1).join("/"), name: names[i] || p.replace(/-/g, " ").replace(/^\w/, (c) => c.toUpperCase()) }));
}
